import type { ReactElement, SyntheticEvent } from "react"
import useFastCheckoutAction from "@gatsby-plugin-basket/hooks/useFastCheckoutAction"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import type { UdsButton } from "@gatsby-plugin-generic-page/fragments/udsButton"
import buildButtonChildren from "@gatsby-plugin-generic-page/helpers/buildButtonChildren"
import type { InjectIconFunction } from "@ionos-web-design-system/icon/types"
import { Button, ButtonIcon } from "@ionos-web-design-system/react"
import { showPlanOverLimitModal } from "~/store/basket/actions"
import { getPlansUsageOverLimit } from "~/store/basket/selectors"
import { useAppDispatch, useAppSelector } from "~/store/hooks"

type UdsFastCheckoutButtonProps = Pick<
  UdsButton,
  "concept" | "variant" | "size"
> & {
  plan: CloudBluePlan[]
  text: string
  title?: string
  className?: string
  onClick?: (event: SyntheticEvent) => void
  buttonType?: UdsButton["buttonType"]
  icon?: InjectIconFunction | null
}

function UdsFastCheckoutButton({
  onClick,
  text,
  title,
  concept,
  variant,
  size,
  plan,
  className,
  buttonType,
  icon,
}: UdsFastCheckoutButtonProps): ReactElement<UdsFastCheckoutButtonProps> {
  const dispatch = useAppDispatch()
  const plansOverLimit = useAppSelector(getPlansUsageOverLimit)
  const { addProductToBasket, isLoading } = useFastCheckoutAction(plan[0])
  const isPlanOverLimit = plansOverLimit.includes(plan[0].flatData.id)

  const handleOnClick = (event: SyntheticEvent) => {
    event.preventDefault()

    if (onClick) {
      onClick(event)
    }

    if (isPlanOverLimit) {
      dispatch(showPlanOverLimitModal())

      return
    }

    addProductToBasket()
  }

  const isSolo = buttonType === "icon-solo" ? icon : null

  const iconNode = icon ? <Button.Icon icon={icon} /> : null

  const buttonChildren = buildButtonChildren(buttonType, iconNode, text)

  return isSolo ? (
    <ButtonIcon
      icon={isSolo}
      concept={concept}
      variant={variant}
      size={size}
      iconTitle={title}
      loading={isLoading}
      onClick={handleOnClick}
      className={className}
    />
  ) : (
    <Button
      concept={concept}
      variant={variant}
      size={size}
      title={title}
      loading={isLoading}
      onClick={handleOnClick}
      className={className}
    >
      {buttonChildren}
    </Button>
  )
}

export default UdsFastCheckoutButton
